import { useEffect, useRef, useState } from 'react'
import './MediaGallerySection.css'
import { VIDEOS } from './mediaGalleryData'

function VideoCard({ video, revealed }) {
  return (
    <a
      href={video.href}
      target="_blank"
      rel="noopener noreferrer"
      className={`mg-card ${video.anim}${revealed ? ' mg-on' : ''}`}
      style={{ animationDelay: video.delay }}
    >
      <div className="mg-thumb">
        <img className="mg-thumb-img" src={video.thumb} alt={video.alt} loading="lazy" />
        <div className="mg-thumb-overlay" />
        <div className="mg-play-idle">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M6 4L16 10L6 16V4Z" fill="rgba(245,158,11,.90)" />
          </svg>
        </div>
        <div className="mg-play-btn">
          <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
            <path d="M6 4L16 10L6 16V4Z" fill="#1C0A00" />
          </svg>
        </div>
        <span className="mg-duration">{video.duration}</span>
      </div>
      <div style={{ padding: '22px 24px 24px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <div className="mg-chip">
            <div className="mg-chip-dot" />
            {video.chip}
          </div>
          <span style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '.14em', color: 'rgba(107,76,42,.45)' }}>Vedic Teaching</span>
        </div>
        <p className="mg-card-title">{video.title}</p>
        <div style={{ flex: 1 }} />
        <span className="mg-watch">
          Watch on YouTube
          <svg className="mg-watch-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M2 7H12M12 7L8 3M12 7L8 11" stroke="#B45309" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
    </a>
  )
}

export default function MediaGallerySection() {
  const sectionRef = useRef(null)
  const [revealed, setRevealed] = useState(false)

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setRevealed(true)
        observer.disconnect()
      },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <section id="media-gallery" className="relative w-full overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div style={{ position: 'absolute', top: '-8%', left: '-4%', width: '50%', height: '60%', background: 'radial-gradient(ellipse at center,rgba(245,158,11,0.08) 0%,transparent 68%)' }} />
        <div style={{ position: 'absolute', bottom: '-6%', right: '-4%', width: '45%', height: '55%', background: 'radial-gradient(ellipse at center,rgba(217,119,6,0.07) 0%,transparent 68%)' }} />
        <div style={{ position: 'absolute', top: '35%', right: '15%', width: '30%', height: '40%', background: 'radial-gradient(ellipse at center,rgba(245,158,11,0.05) 0%,transparent 65%)' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(180,83,9,0.06) 1px,transparent 1px)', backgroundSize: '36px 36px' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(180,83,9,.18),transparent)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(180,83,9,.12),transparent)' }} />
        <svg style={{ position: 'absolute', top: '-70px', right: '-70px', width: '340px', height: '340px', opacity: 0.07 }} viewBox="0 0 340 340" fill="none">
          <circle cx="170" cy="170" r="148" stroke="#F59E0B" strokeWidth="1.5" />
          <circle cx="170" cy="170" r="116" stroke="#D97706" strokeWidth="0.8" strokeDasharray="3 6" />
          <circle cx="170" cy="170" r="84" stroke="#B45309" strokeWidth="0.5" />
        </svg>
        <svg style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '280px', height: '280px', opacity: 0.06 }} viewBox="0 0 280 280" fill="none">
          <circle cx="140" cy="140" r="118" stroke="#F59E0B" strokeWidth="1.2" />
          <circle cx="140" cy="140" r="88" stroke="#D97706" strokeWidth="0.7" strokeDasharray="2 5" />
        </svg>
        <div style={{ position: 'absolute', top: '18px', left: '18px', width: '72px', height: '72px', pointerEvents: 'none', opacity: 0.6 }}>
          <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
            <path d="M62 5 L28 5 Q5 5 5 28 L5 62" stroke="rgba(180,83,9,.55)" strokeWidth="1.2" strokeLinecap="round" />
            <circle cx="5" cy="5" r="3" stroke="rgba(180,83,9,.50)" strokeWidth="1" fill="rgba(180,83,9,.10)" />
            <circle cx="5" cy="5" r="1.2" fill="rgba(180,83,9,.65)" />
          </svg>
        </div>
        <div style={{ position: 'absolute', bottom: '18px', right: '18px', width: '72px', height: '72px', pointerEvents: 'none', opacity: 0.6 }}>
          <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
            <g transform="translate(72,72) scale(-1,-1)">
              <path d="M62 5 L28 5 Q5 5 5 28 L5 62" stroke="rgba(180,83,9,.55)" strokeWidth="1.2" strokeLinecap="round" />
              <circle cx="5" cy="5" r="3" stroke="rgba(180,83,9,.50)" strokeWidth="1" fill="rgba(180,83,9,.10)" />
              <circle cx="5" cy="5" r="1.2" fill="rgba(180,83,9,.65)" />
            </g>
          </svg>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`text-center mg-au${revealed ? ' mg-on' : ''}`} style={{ marginBottom: '60px' }}>
          <div className="mg-label">
            <span>Sacred Visuals</span>
          </div>
          <h2 className="mg-title">
            Divine Media <span>Gallery</span>
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', margin: '0 auto 18px', maxWidth: '260px' }}>
            <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(180,83,9,.35))' }} />
            <span style={{ fontFamily: 'var(--font-heading),serif', fontSize: '14px', color: 'rgba(180,83,9,.42)' }}>ॐ</span>
            <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg,rgba(180,83,9,.35),transparent)' }} />
          </div>
          <p className="mg-desc">
            Explore profound astrological teachings, sacred Vedic insights, and spiritual guidance through our
            curated collection of video content.
          </p>
        </div>

        <div
          id="mg-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          style={{ gap: '28px', marginBottom: '56px' }}
        >
          {VIDEOS.map((video) => (
            <VideoCard video={video} revealed={revealed} key={video.href} />
          ))}
        </div>

        <div className={`text-center mg-au${revealed ? ' mg-on' : ''}`} style={{ animationDelay: '.36s' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
              <rect width="18" height="18" rx="5" fill="#FF0000" opacity="0.88" />
              <path d="M7 6L13 9L7 12V6Z" fill="#FFFFFF" />
            </svg>
            <span style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '.24em', color: 'rgba(107,76,42,.50)' }}>
              @astrologerrajumaharaj · 100+ videos
            </span>
          </div>
          <a href="https://youtube.com/@astrologerrajumaharaj?si=C9gmufusTxw5K0cg" target="_blank" rel="noopener noreferrer" className="mg-cta" style={{ position: 'relative', zIndex: 1 }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
              <rect width="18" height="18" rx="5" fill="#FF0000" opacity="0.90" />
              <path d="M7 6L13 9L7 12V6Z" fill="#FFFFFF" />
            </svg>
            <span style={{ position: 'relative', zIndex: 1 }}>View YouTube Channel</span>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ position: 'relative', zIndex: 1 }}>
              <path d="M2 7H12M12 7L8 3M12 7L8 11" stroke="#F5EBD6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  )
}
